import { ClientProps, PriceProductProps } from '@/types';

export type PackagesTypesProps = 'UN' | 'CX' | 'DZ' | 'FD' | 'KG' | 'PK' | string;

/** Segmentos especiais no arquivo de preço (ex.: hierarquia data > loja > segmento > produto). 998 = faixa representante; 999 = geral. */
export function isNroSegmentoPrecoCuringa(nro: number | null | undefined): boolean {
  const n = Number(nro);
  return n === 998 || n === 999;
}

interface EffectivePriceParams {
  prices: PriceProductProps[];
  pack: PackagesTypesProps;
  packQty: number;
  selectedClient: ClientProps;
  paymentCode: number;
}

/**
 * Try to find the direct price record for the given pack/quantity/payment.
 */
export function findDirectPriceRecord({ prices, pack, packQty, paymentCode, selectedClient }: EffectivePriceParams): PriceProductProps | null {
  const direct = prices.find((p) => {
    const clientSegs = selectedClient?.COD_SEGMENTO?.map((s: any) => s.cod_segmento) || [];
    const segs =
      isNroSegmentoPrecoCuringa(p?.NRO_SEGMENTO) ||
      clientSegs.some((s: number) => Number(s) === Number(p?.NRO_SEGMENTO));
    if (pack === 'PK') {
      return p.SG_EMBALAGEM === pack && p.QTD_MULTIPLO_VENDA === packQty && p.NROCONDICAOPAGTO === paymentCode && segs;
    }
    return p.SG_EMBALAGEM === pack && p.QTD_EMBALAGEM === packQty && p.NROCONDICAOPAGTO === paymentCode && segs;
  });
  return (direct as PriceProductProps) || null;
}

/**
 * When there is no direct price for the requested pack/quantity/payment,
 * build a synthetic record by multiplying the base 'UN' record by the pack quantity.
 * This preserves the main fiscal fields so ST can still be calculated downstream.
 */
export function buildFallbackFromUnit({ prices, pack, packQty, paymentCode }: EffectivePriceParams): PriceProductProps | null {
  if (!Array.isArray(prices) || !prices.length || !packQty) return null;

  const baseUnit = prices.find(
    (p) => p.SG_EMBALAGEM === 'UN' && p.NROCONDICAOPAGTO === paymentCode
  ) as PriceProductProps | undefined;

  if (!baseUnit || typeof baseUnit.VLR_FINAL !== 'number') return null;

  const multiplier = packQty || 1;
  const multiply = (val?: number) => (typeof val === 'number' ? Number((val * multiplier).toFixed(2)) : val);

  const computed: PriceProductProps = {
    ...baseUnit,
    SG_EMBALAGEM: pack,
    QTD_MULTIPLO_VENDA: pack === 'PK' ? multiplier : baseUnit.QTD_MULTIPLO_VENDA,
    QTD_EMBALAGEM: pack === 'PK' ? baseUnit.QTD_EMBALAGEM : multiplier,
    NROCONDICAOPAGTO: paymentCode,
    VLR_FINAL: multiply(baseUnit.VLR_FINAL) as number,
    VLR_PRECO: multiply(baseUnit.VLR_PRECO) as number,
    VLR_PAUTA: multiply(baseUnit.VLR_PAUTA) as number,
    VLR_PRECO_CALCULADO: multiply(baseUnit.VLR_PRECO_CALCULADO) as number,
    // ST_VLR_FINAL remains the original per-unit ST. Consumers (StPrice) recompute ST with precoCalculado/codTrib when needed.
  } as PriceProductProps;

  return computed;
}

export interface EffectivePriceResult {
  effectiveRecord: PriceProductProps | null;
  effectiveFinalPrice: number; // VLR_FINAL to be used
  unitPrice: number;
}

/**
 * Compute an "effective" price record for UI/validation purposes.
 * Prefers the direct price; if missing, falls back to unit price * pack quantity.
 */
export function getEffectivePriceRecordForPack(params: EffectivePriceParams): EffectivePriceResult {
  const { prices, pack, packQty, paymentCode, selectedClient } = params;

  const direct = findDirectPriceRecord({ prices, pack, packQty, paymentCode, selectedClient });
  const fallback = direct ? null : buildFallbackFromUnit({ prices, pack, packQty, paymentCode, selectedClient });
  const record = direct || fallback || null;

  const final = record?.VLR_FINAL || 0;
  const qty = packQty || 0;
  const unit = final && qty ? Number((final / qty).toFixed(2)) : 0;

  return { effectiveRecord: record, effectiveFinalPrice: final, unitPrice: unit };
}
